import { type ValueOf } from 'type-fest';
import { Service } from 'typedi';

export const CcmCustomAttribute = {
  ERROR_BOUNDARY: 'ccmErrorBoundary',
  ERROR_METADATA: 'ccmErrorMetadata',
  ERROR_ROUTE: 'ccmErrorRoute',
} as const;

export type CcmCustomAttribute = ValueOf<typeof CcmCustomAttribute>;

export type NewRelic = {
  addRelease: (releaseName: string, releaseId: string) => void;
  noticeError: (
    error: Error,
    customAttributes?: Record<string, string>,
  ) => void;
  setCustomAttribute: (name: string, value: number | string | null) => void;
};

declare const newrelic: NewRelic | undefined;

export const isError = (error: unknown): error is Error => {
  return (
    (typeof error === 'object' || typeof error === 'function') &&
    error !== null &&
    'message' in error &&
    'name' in error
  );
};

const newRelicNotLoadedMessage =
  'New Relic is not loaded.  Errors will not be reported.';

const fallbackNewRelic: NewRelic = {
  addRelease: (releaseName, releaseId) => {
    console.warn(newRelicNotLoadedMessage);
    console.warn(releaseName, releaseId);
  },
  noticeError: (error) => {
    console.warn(newRelicNotLoadedMessage);
    console.error(error.message);
    console.error(error.stack);
  },
  setCustomAttribute: (name, value) => {
    console.warn(newRelicNotLoadedMessage);
    console.warn(name, value);
  },
};

const getNewRelic = (): NewRelic => {
  try {
    return newrelic ?? fallbackNewRelic;
  } catch {
    console.warn(newRelicNotLoadedMessage);
    return fallbackNewRelic;
  }
};

@Service()
export class LoggerService {
  check(condition: boolean, msg?: string): void {
    if (!condition) {
      const error = new Error(
        `Check Error: ${msg ?? 'no additional info provided'}`,
      );
      this.error(error);
    }
  }

  clearCustomAttribute(key: CcmCustomAttribute): void {
    getNewRelic().setCustomAttribute(key, null);
  }

  /*
    This error method will log the details in the 'JS errors' page in the New
    Relic web application. This can also be filtered in the event explore in New
    relic.
  */
  error(
    e: unknown,
    attributes?: Partial<Record<CcmCustomAttribute, string>>,
  ): void {
    if (isError(e)) {
      getNewRelic().noticeError(e, attributes);
    } else {
      getNewRelic().noticeError(new Error(e as string), attributes);
    }
  }

  /*
    This setCustomAttribute method will log the data point and could be used to
    filter the 'Page Views' by the group by criteria, and also will be helpful
    to filter the data in the event explore in New relic

    *** Make sure no user data is store in the logs ***
  */
  setCustomAttribute(key: CcmCustomAttribute, value: number | string): void {
    getNewRelic().setCustomAttribute(key, value);
  }
}
